/** Encrypted local persistence — SPEC §16 (IndexedDB / Dexie). */

import Dexie, { type Table } from "dexie";
import type {
  Account,
  Contact,
  Conversation,
  DraftRecord,
  GroupRecord,
  MessageEvent,
  SecurityEvent,
} from "@veto/domain";
import {
  openField,
  sealField,
  setAtRestCipher,
  getAtRestCipher,
  isSealedField,
  ENC_PREFIX,
} from "./atRestCipher";

export { setAtRestCipher, getAtRestCipher, isSealedField, ENC_PREFIX } from "./atRestCipher";

export interface VaultRecord {
  id: string;
  wrappedJson: string;
  locked: boolean;
}

export interface MetaRecord {
  key: string;
  value: string;
}

export interface SessionRecord {
  conversationId: string;
  sealedStateJson: string;
  updatedAt: number;
}

/**
 * Durable wire outbox (protocol v2, FR-MSG-001 / SPEC §12). One row per
 * (message, recipient): the fully sealed + signed transport event is stored and
 * republished VERBATIM on retry — never re-encrypted (re-sealing would consume a
 * new ratchet key and produce a duplicate). Covers texts, group fan-out copies,
 * edits, reactions, deletes, profile and control messages.
 */
export interface OutboxRecord {
  /** `${messageEventId}:${recipientId}` */
  id: string;
  /** App message id (UI row) — aggregated status target. */
  messageEventId: string;
  /** Conversation the UI row lives in (direct or group). */
  conversationId: string;
  /** Recipient contact id. */
  recipientId: string;
  /** Signed NIP-01 event JSON (ciphertext only) — sealed at rest. */
  eventJson: string;
  createdAt: number;
  /** Number of recipients the message was fanned out to (aggregate status). */
  fanout: number;
  attempts: number;
  nextAt: number;
  status: "queued" | "sent" | "backed_up";
  transport?: string;
  /** false for control traffic (reactions, receipts…) that has no UI row. */
  tracksMessage: boolean;
}

/**
 * Inbound journal (review M2): the decrypted app payload is written in the SAME
 * transaction as the advanced ratchet state, so a crash between "message key
 * consumed" and "message stored" can no longer lose the message. Rows are
 * replayed through the handlers after unlock and deleted once handled.
 */
export interface InboxRecord {
  /** Transport event id. */
  id: string;
  conversationId: string;
  createdAt: number;
  attempts: number;
  /** Sealed JSON (see apps/web inboundProcessor `JournalEntry`). */
  sealedJson: string;
}

/** Transport event ids already handled — survives reloads (relay catch-up re-sends). */
export interface ProcessedRecord {
  id: string;
  at: number;
}

export class VetoDB extends Dexie {
  account!: Table<Account, string>;
  contacts!: Table<Contact, string>;
  conversations!: Table<Conversation, string>;
  messages!: Table<MessageEvent, string>;
  vault!: Table<VaultRecord, string>;
  meta!: Table<MetaRecord, string>;
  sessions!: Table<SessionRecord, string>;
  securityEvents!: Table<SecurityEvent, string>;
  drafts!: Table<DraftRecord, string>;
  groups!: Table<GroupRecord, string>;
  outbox!: Table<OutboxRecord, string>;
  inbox!: Table<InboxRecord, string>;
  processed!: Table<ProcessedRecord, string>;

  constructor(name = "veto-db-v1") {
    super(name);
    this.version(1).stores({
      account: "rootId",
      contacts: "id, messagingPublicKeyHex",
      conversations: "id, lastMessageAt",
      messages: "eventId, conversationId, createdAt",
      vault: "id",
      meta: "key",
      sessions: "conversationId",
    });
    this.version(2).stores({
      account: "rootId",
      contacts: "id, messagingPublicKeyHex",
      conversations: "id, lastMessageAt",
      messages: "eventId, conversationId, createdAt",
      vault: "id",
      meta: "key",
      sessions: "conversationId",
      securityEvents: "id, createdAt, acknowledged",
    });
    this.version(3).stores({
      account: "rootId",
      contacts: "id, messagingPublicKeyHex",
      conversations: "id, lastMessageAt",
      messages: "eventId, conversationId, createdAt",
      vault: "id",
      meta: "key",
      sessions: "conversationId",
      securityEvents: "id, createdAt, acknowledged",
      drafts: "conversationId",
    });
    this.version(4).stores({
      account: "rootId",
      contacts: "id, messagingPublicKeyHex",
      conversations: "id, lastMessageAt",
      messages: "eventId, conversationId, createdAt",
      vault: "id",
      meta: "key",
      sessions: "conversationId",
      securityEvents: "id, createdAt, acknowledged",
      drafts: "conversationId",
      groups: "groupId, conversationId",
    });
    // v5: additive `expiresAt` index for the durable disappearing-message
    // sweep (FR-MSG-004). No data rewrite; older rows simply lack the index key.
    this.version(5).stores({
      account: "rootId",
      contacts: "id, messagingPublicKeyHex",
      conversations: "id, lastMessageAt",
      messages: "eventId, conversationId, createdAt, expiresAt",
      vault: "id",
      meta: "key",
      sessions: "conversationId",
      securityEvents: "id, createdAt, acknowledged",
      drafts: "conversationId",
      groups: "groupId, conversationId",
    });
    // v6: protocol-v2 wire outbox (per-recipient sealed events).
    this.version(6).stores({
      account: "rootId",
      contacts: "id, messagingPublicKeyHex",
      conversations: "id, lastMessageAt",
      messages: "eventId, conversationId, createdAt, expiresAt",
      vault: "id",
      meta: "key",
      sessions: "conversationId",
      securityEvents: "id, createdAt, acknowledged",
      drafts: "conversationId",
      groups: "groupId, conversationId",
      outbox: "id, messageEventId, conversationId, nextAt",
    });
    // v7: inbound journal + processed event ids (review M2 / recovery dedupe).
    this.version(7).stores({
      account: "rootId",
      contacts: "id, messagingPublicKeyHex",
      conversations: "id, lastMessageAt",
      messages: "eventId, conversationId, createdAt, expiresAt",
      vault: "id",
      meta: "key",
      sessions: "conversationId",
      securityEvents: "id, createdAt, acknowledged",
      drafts: "conversationId",
      groups: "groupId, conversationId",
      outbox: "id, messageEventId, conversationId, nextAt",
      inbox: "id, createdAt",
      processed: "id, at",
    });
  }
}

export let db = new VetoDB();

/**
 * Another tab is upgrading (or wiping) the database. Dexie's default handler
 * already closes this connection so the other tab can proceed; we only tell
 * the app, which reloads into the new version instead of failing later on a
 * stale schema.
 */
function watchVersionChange(d: VetoDB): void {
  d.on("versionchange", () => {
    try {
      globalThis.dispatchEvent?.(new Event("veto:db-upgraded"));
    } catch {
      /* non-browser */
    }
  });
}
watchVersionChange(db);

/**
 * Test-only: point every storage function at another database so two simulated
 * clients can run in one process (their pairwise conversation ids collide).
 */
const testDatabases = new Map<string, VetoDB>();
export function __useDatabaseForTests(name: string): void {
  let next = testDatabases.get(name);
  if (!next) {
    next = new VetoDB(name);
    testDatabases.set(name, next);
  }
  db = next;
}

export async function putMessage(msg: MessageEvent): Promise<void> {
  await db.messages.put(msg);
}

/**
 * Insert only if no row has this id — atomic (no check-then-put race). Inbound
 * rows must never replace existing ones (review L2).
 */
export async function addMessageIfAbsent(msg: MessageEvent): Promise<boolean> {
  try {
    await db.messages.add(msg);
    return true;
  } catch (err) {
    if (err instanceof Dexie.ConstraintError || (err as { name?: string })?.name === "ConstraintError") return false;
    throw err;
  }
}

export async function listMessages(conversationId: string): Promise<MessageEvent[]> {
  return db.messages.where("conversationId").equals(conversationId).sortBy("createdAt");
}

export async function upsertConversation(c: Conversation): Promise<void> {
  const row: Conversation = {
    ...c,
    lastMessagePreview: await sealField(c.lastMessagePreview),
  };
  await db.conversations.put(row);
}

export async function listConversations(): Promise<Conversation[]> {
  const rows = await db.conversations.orderBy("lastMessageAt").reverse().toArray();
  return Promise.all(
    rows.map(async (c) => ({
      ...c,
      lastMessagePreview: await openField(c.lastMessagePreview),
    })),
  );
}

/**
 * Delete a conversation and every local trace of its CONTENT: messages, draft,
 * group record and per-chat meta (chat lock, disappear timer). The encryption
 * session is deliberately kept (review H2/M1): wiping it re-opened old
 * first-contact messages to replay and silently dropped everything the contact
 * sent next. Contacts, security events and account data are kept too.
 */
export async function deleteConversationData(conversationId: string): Promise<void> {
  await db.transaction(
    "rw",
    [db.conversations, db.messages, db.drafts, db.groups, db.meta],
    async () => {
      await db.conversations.delete(conversationId);
      await db.messages.where("conversationId").equals(conversationId).delete();
      await db.drafts.delete(conversationId);
      const group = await db.groups.where("conversationId").equals(conversationId).first();
      if (group) await db.groups.delete(group.groupId);
      await db.meta.delete(`chatlock.${conversationId}`);
      await db.meta.delete(`disappear.${conversationId}`);
    },
  );
  await db.outbox.where("conversationId").equals(conversationId).delete();
}

/** Clear message history but keep the conversation row and its settings (pin/mute/timers). */
export async function clearConversationMessages(conversationId: string): Promise<void> {
  await db.transaction("rw", db.messages, db.conversations, async () => {
    await db.messages.where("conversationId").equals(conversationId).delete();
    const conv = await db.conversations.get(conversationId);
    if (conv) {
      await db.conversations.put({ ...conv, lastMessagePreview: await sealField("") });
    }
  });
}

/** Account/Contact rows carry profile fields sealed at rest as aboutEnc/avatarEnc (SPEC §16). */
type ProfileRow = { aboutEnc?: string; avatarEnc?: string };

async function sealProfileFields<T extends { about?: string; avatar?: { dataB64?: string } | null }>(
  item: T,
): Promise<T & ProfileRow> {
  const { about, avatar, ...rest } = item;
  const row = rest as T & ProfileRow;
  if (typeof about === "string" && about.length > 0) {
    row.aboutEnc = await sealField(about);
  }
  if (avatar && typeof avatar.dataB64 === "string" && avatar.dataB64.length > 0) {
    row.avatarEnc = await sealField(JSON.stringify(avatar));
  }
  return row;
}

/**
 * Account/Contact rows only *optionally* carry sealed profile fields, so the
 * generic is intentionally loose (`object`) and presence is checked at runtime.
 */
async function openProfileRow<T extends object>(
  row: T,
): Promise<T & { about?: string; avatar?: unknown }> {
  const out = { ...row } as T & { about?: string; avatar?: unknown; aboutEnc?: string; avatarEnc?: string };
  if (typeof out.aboutEnc === "string") {
    const opened = await openField(out.aboutEnc);
    if (!opened.startsWith("[unable")) out.about = opened;
  }
  if (typeof out.avatarEnc === "string") {
    try {
      const opened = await openField(out.avatarEnc);
      if (!opened.startsWith("[unable")) out.avatar = JSON.parse(opened);
    } catch {
      /* corrupt profile media — drop rather than fail unlock */
    }
  }
  delete out.aboutEnc;
  delete out.avatarEnc;
  return out;
}

export async function getAccount(): Promise<Account | undefined> {
  const row = await db.account.toCollection().first();
  if (!row) return undefined;
  return (await openProfileRow(row)) as Account;
}

export async function saveAccount(account: Account): Promise<void> {
  await db.account.put(await sealProfileFields(account) as Account);
}

export async function saveContact(contact: Contact): Promise<void> {
  const row = await sealProfileFields(contact);
  await db.contacts.put({
    ...row,
    messagingPublicKeyHex: await sealField(contact.messagingPublicKeyHex),
    hybridPublicKeyHex: await sealField(contact.hybridPublicKeyHex),
    safetyNumber: await sealField(contact.safetyNumber),
  } as Contact);
}

export async function listContacts(): Promise<Contact[]> {
  const rows = await db.contacts.toArray();
  return Promise.all(
    rows.map(async (c) => {
      const opened = (await openProfileRow(c)) as Contact & ProfileRow;
      return {
        ...opened,
        messagingPublicKeyHex: await openField(c.messagingPublicKeyHex),
        hybridPublicKeyHex: await openField(c.hybridPublicKeyHex),
        safetyNumber: await openField(c.safetyNumber),
      };
    }),
  );
}

export async function saveVault(wrappedJson: string): Promise<void> {
  await db.vault.put({ id: "primary", wrappedJson, locked: true });
}

export async function getVault(): Promise<VaultRecord | undefined> {
  return db.vault.get("primary");
}

export async function setMeta(key: string, value: string): Promise<void> {
  await db.meta.put({ key, value });
}

export async function getMeta(key: string): Promise<string | undefined> {
  return (await db.meta.get(key))?.value;
}

export async function delMeta(key: string): Promise<void> {
  await db.meta.delete(key);
}

/**
 * Durable disappearing-message enforcement (FR-MSG-004).
 * The 5s UI sweep only blanks the open chat in memory — without this,
 * expired ciphertext + previews survive in IndexedDB indefinitely and a
 * post-unlock forensic read recovers "disappeared" bodies.
 *
 * Deletes expired rows (indexed `expiresAt` query, no decryption needed) and
 * blanks affected conversation previews that may quote purged bodies.
 * Unsent outbox rows (out + queued/draft/sent) are NEVER destroyed — expiry
 * must not eat user data that never left the device.
 */
const UNSENT_OUTBOX_STATUS = new Set(["queued", "draft", "sent"]);

export async function purgeExpiredMessages(
  now = Date.now(),
): Promise<{ purged: number; conversationIds: string[] }> {
  const expired = await db.messages.where("expiresAt").belowOrEqual(now).toArray();
  const doomed = expired.filter(
    (m) => !(m.direction === "out" && UNSENT_OUTBOX_STATUS.has(m.status)),
  );
  if (doomed.length === 0) return { purged: 0, conversationIds: [] };
  const conversationIds = [...new Set(doomed.map((m) => m.conversationId))];
  await db.messages.bulkDelete(doomed.map((m) => m.eventId));
  for (const id of conversationIds) {
    const conv = await db.conversations.get(id);
    if (conv?.lastMessagePreview) {
      await db.conversations.put({ ...conv, lastMessagePreview: await sealField("") });
    }
  }
  return { purged: doomed.length, conversationIds };
}

// ---------------------------------------------------------------------------
// Wire outbox (protocol v2)
// ---------------------------------------------------------------------------

export async function putOutbox(rec: OutboxRecord): Promise<void> {
  await db.outbox.put({ ...rec, eventJson: await sealField(rec.eventJson) });
}

async function openOutbox(rec: OutboxRecord): Promise<OutboxRecord> {
  return { ...rec, eventJson: await openField(rec.eventJson) };
}

/** Rows due for (re)publish, oldest first. */
export async function listDueOutbox(now = Date.now(), limit = 50): Promise<OutboxRecord[]> {
  const rows = await db.outbox.where("nextAt").belowOrEqual(now).limit(limit).toArray();
  return Promise.all(rows.map(openOutbox));
}

export async function listOutboxForMessage(messageEventId: string): Promise<OutboxRecord[]> {
  const rows = await db.outbox.where("messageEventId").equals(messageEventId).toArray();
  return Promise.all(rows.map(openOutbox));
}

/** Update delivery bookkeeping without touching the sealed event. */
export async function updateOutboxStatus(
  id: string,
  patch: Partial<Pick<OutboxRecord, "attempts" | "nextAt" | "status" | "transport">>,
): Promise<void> {
  await db.outbox.update(id, patch);
}

export async function deleteOutbox(id: string): Promise<void> {
  await db.outbox.delete(id);
}

export async function countOutbox(): Promise<number> {
  return db.outbox.count();
}

export async function clearOutbox(): Promise<void> {
  await db.outbox.clear();
}

/** FR-MSG-001 crash/restart helper */
export async function listQueuedOutbox(): Promise<MessageEvent[]> {
  const all = await db.messages.toArray();
  return all.filter((m) => m.direction === "out" && (m.status === "queued" || m.status === "draft" || m.status === "sent"));
}

export type PersistedSession = {
  handshakeJson?: string;
  /** Serialized RatchetSession — required to prevent handshake re-init / replay after reload. */
  ratchetStateJson?: string;
};

export async function saveSessionState(
  conversationId: string,
  state: PersistedSession,
): Promise<void> {
  const prev = await loadSessionState(conversationId);
  const plain = JSON.stringify({
    handshakeJson: state.handshakeJson ?? prev?.handshakeJson,
    ratchetStateJson: state.ratchetStateJson ?? prev?.ratchetStateJson,
  });
  // AUDIT-WEB-003: seal ratchet roots/chains at rest when vault cipher is active.
  await db.sessions.put({
    conversationId,
    sealedStateJson: await sealField(plain),
    updatedAt: Date.now(),
  });
}

/**
 * Protocol-v2 session record write (no read-merge: called on every message).
 * Stored in the same sealed slot as v1 state; v1 JSON fails to import as a v2
 * record and is discarded, which is the v1→v2 session migration.
 *
 * Optionally commits, in ONE transaction: the replay-cache copy kept outside the
 * record (review H2) and an inbound journal row (review M2). Everything is sealed
 * before the transaction opens (WebCrypto awaits cannot run inside it).
 */
export async function saveSessionRecordJson(
  conversationId: string,
  recordJson: string,
  extra?: {
    replayCacheB64?: string;
    journal?: { id: string; json: string };
  },
): Promise<void> {
  const sealedState = await sealField(JSON.stringify({ ratchetStateJson: recordJson }));
  const sealedGuard = extra?.replayCacheB64 ? await sealField(extra.replayCacheB64) : null;
  const sealedJournal = extra?.journal ? await sealField(extra.journal.json) : null;
  const now = Date.now();
  await db.transaction("rw", [db.sessions, db.meta, db.inbox], async () => {
    await db.sessions.put({ conversationId, sealedStateJson: sealedState, updatedAt: now });
    if (sealedGuard) await db.meta.put({ key: `replay.${conversationId}`, value: sealedGuard });
    if (sealedJournal && extra?.journal) {
      await db.inbox.put({
        id: extra.journal.id,
        conversationId,
        createdAt: now,
        attempts: 0,
        sealedJson: sealedJournal,
      });
    }
  });
}

/** Replay-cache copy for a conversation (base64), if one was ever stored. */
export async function loadReplayGuard(conversationId: string): Promise<string | undefined> {
  const row = await db.meta.get(`replay.${conversationId}`);
  if (!row) return undefined;
  try {
    const opened = await openField(row.value);
    return opened.startsWith("[unable") ? undefined : opened;
  } catch {
    return undefined;
  }
}

/** True when a (possibly unreadable) session row exists. */
export async function sessionRowExists(conversationId: string): Promise<boolean> {
  return (await db.sessions.get(conversationId)) !== undefined;
}

/** Keep an unreadable session row for diagnosis instead of overwriting it. */
export async function quarantineSessionRow(conversationId: string): Promise<void> {
  const row = await db.sessions.get(conversationId);
  if (row) await db.meta.put({ key: `session.quarantine.${conversationId}.${Date.now()}`, value: row.sealedStateJson });
}

export async function listSessionConversationIds(): Promise<string[]> {
  return (await db.sessions.toCollection().primaryKeys()) as string[];
}

export async function listInbox(): Promise<InboxRecord[]> {
  return db.inbox.orderBy("createdAt").toArray();
}

export async function openInboxRow(row: InboxRecord): Promise<string | null> {
  try {
    const opened = await openField(row.sealedJson);
    return opened.startsWith("[unable") ? null : opened;
  } catch {
    return null;
  }
}

export async function deleteInbox(id: string): Promise<void> {
  await db.inbox.delete(id);
}

export async function bumpInboxAttempts(id: string): Promise<number> {
  const row = await db.inbox.get(id);
  if (!row) return 0;
  await db.inbox.update(id, { attempts: row.attempts + 1 });
  return row.attempts + 1;
}

export async function markEventsProcessed(ids: string[]): Promise<void> {
  if (!ids.length) return;
  const at = Date.now();
  await db.processed.bulkPut(ids.map((id) => ({ id, at })));
}

/** Subset of `ids` already handled in an earlier run. */
export async function processedAmong(ids: string[]): Promise<Set<string>> {
  if (!ids.length) return new Set();
  const rows = await db.processed.bulkGet(ids);
  return new Set(rows.filter((r): r is ProcessedRecord => !!r).map((r) => r.id));
}

/** Forget processed ids older than the relay catch-up window. */
export async function pruneProcessed(maxAgeMs = 35 * 24 * 60 * 60 * 1000): Promise<number> {
  return db.processed.where("at").below(Date.now() - maxAgeMs).delete();
}

export async function deleteSessionState(conversationId: string): Promise<void> {
  await db.sessions.delete(conversationId);
}

/** Delivery bookkeeping on a stored message without touching its sealed body. */
export async function updateMessageDelivery(
  eventId: string,
  patch: Partial<Pick<MessageEvent, "status" | "transport">>,
): Promise<void> {
  await db.messages.update(eventId, patch);
}

export async function getMessageRaw(eventId: string): Promise<MessageEvent | undefined> {
  return db.messages.get(eventId);
}

/** @deprecated prefer saveSessionState — kept for call-site migration */
export async function saveHandshake(conversationId: string, handshakeJson: string): Promise<void> {
  await saveSessionState(conversationId, { handshakeJson });
}

export async function loadSessionState(conversationId: string): Promise<PersistedSession | undefined> {
  const row = await db.sessions.get(conversationId);
  if (!row?.sealedStateJson) return undefined;
  try {
    const opened = await openField(row.sealedStateJson);
    if (opened.startsWith("[unable")) return undefined;
    return JSON.parse(opened) as PersistedSession;
  } catch {
    return undefined;
  }
}

export async function loadHandshake(conversationId: string): Promise<string | undefined> {
  return (await loadSessionState(conversationId))?.handshakeJson;
}

/**
 * FR-DEV-003 durable rotation: delete ALL persisted ratchet states so a
 * revoked device's chains cannot be resurrected via restoreSession() after
 * the in-memory wipe. The next send re-initiates a fresh handshake
 * (openSessionForContact falls back to initiatePair), which is the
 * forward-secrecy-friendly recovery the revoke toast promises.
 */
export async function clearAllSessionStates(): Promise<number> {
  const count = await db.sessions.count();
  await db.sessions.clear();
  return count;
}

export async function addSecurityEvent(
  event: Omit<SecurityEvent, "id" | "createdAt" | "acknowledged"> & Partial<Pick<SecurityEvent, "id">>,
): Promise<void> {
  const full: SecurityEvent = {
    id: event.id ?? crypto.randomUUID(),
    kind: event.kind,
    contactId: event.contactId,
    detail: event.detail,
    createdAt: Date.now(),
    acknowledged: false,
  };
  await db.securityEvents.put(full);
}

export async function listSecurityEvents(): Promise<SecurityEvent[]> {
  return db.securityEvents.orderBy("createdAt").reverse().toArray();
}

export async function acknowledgeSecurityEvent(id: string): Promise<void> {
  const row = await db.securityEvents.get(id);
  if (!row) return;
  await db.securityEvents.put({ ...row, acknowledged: true });
}

export async function getContact(id: string): Promise<Contact | undefined> {
  const c = await db.contacts.get(id);
  if (!c) return undefined;
  const opened = (await openProfileRow(c)) as Contact & ProfileRow;
  return {
    ...opened,
    messagingPublicKeyHex: await openField(c.messagingPublicKeyHex),
    hybridPublicKeyHex: await openField(c.hybridPublicKeyHex),
    safetyNumber: await openField(c.safetyNumber),
  };
}

/** Scan stored rows for forbidden plaintext secret markers (H2). */
export async function scanForForbiddenSecrets(): Promise<string[]> {
  const hits: string[] = [];
  const forbidden = [/vault_key_b64/i, /"seedsHex"\s*:\s*"[0-9a-f]{32,}"/i];
  const tables = [
    await db.outbox.toArray(),
    await db.vault.toArray(),
    await db.meta.toArray(),
    await db.account.toArray(),
    await db.contacts.toArray(),
    await db.conversations.toArray(),
    await db.messages.toArray(),
    await db.sessions.toArray(),
    await db.groups.toArray(),
  ];
  for (const rows of tables) {
    for (const row of rows) {
      const blob = JSON.stringify(row);
      for (const re of forbidden) {
        if (re.test(blob)) hits.push(blob.slice(0, 120));
      }
      // Contact key material must be sealed when cipher is active
      if (getAtRestCipher() && "messagingPublicKeyHex" in (row as object)) {
        const c = row as Contact;
        if (c.messagingPublicKeyHex && !c.messagingPublicKeyHex.startsWith("enc:v1:")) {
          hits.push(`plaintext contact key ${c.id}`);
        }
      }
      // Profile fields (bio + avatar media) must be sealed when cipher is active.
      if (getAtRestCipher()) {
        const p = row as Partial<ProfileRow> & { id?: string; rootId?: string };
        const rowId = p.id ?? p.rootId ?? "?";
        if (typeof p.aboutEnc === "string" && !p.aboutEnc.startsWith(ENC_PREFIX)) {
          hits.push(`plaintext profile about ${rowId}`);
        }
        if (typeof p.avatarEnc === "string" && !p.avatarEnc.startsWith(ENC_PREFIX)) {
          hits.push(`plaintext profile avatar ${rowId}`);
        }
      }
      if (getAtRestCipher() && "senderKeyB64" in (row as object)) {
        const g = row as GroupRecord;
        if (g.senderKeyB64 && !g.senderKeyB64.startsWith(ENC_PREFIX)) {
          hits.push(`plaintext group sender key ${g.groupId}`);
        }
      }
      // My stickers are user media: always sealed at rest.
      const meta = row as Partial<MetaRecord>;
      if (
        getAtRestCipher() &&
        typeof meta.key === "string" &&
        meta.key.startsWith(MY_STICKER_META_PREFIX) &&
        typeof meta.value === "string" &&
        !isSealedField(meta.value)
      ) {
        hits.push(`plaintext sticker ${meta.key}`);
      }
    }
  }
  return hits;
}

export async function saveDraft(draft: DraftRecord): Promise<void> {
  if (!draft.text.trim() && !draft.replyToEventId) {
    await db.drafts.delete(draft.conversationId);
    return;
  }
  // AUDIT-WEB-005: seal draft plaintext at rest when vault cipher is active.
  await db.drafts.put({
    ...draft,
    text: await sealField(draft.text),
    updatedAt: Date.now(),
  });
}

export async function loadDraft(conversationId: string): Promise<DraftRecord | undefined> {
  const row = await db.drafts.get(conversationId);
  if (!row) return undefined;
  return {
    ...row,
    text: await openField(row.text),
  };
}

export async function saveGroup(group: GroupRecord): Promise<void> {
  // Seal sender keys at rest when vault cipher is active (SEC finding H-2).
  await db.groups.put({
    ...group,
    senderKeyB64: await sealField(group.senderKeyB64),
  });
}

async function openGroupRow(group: GroupRecord): Promise<GroupRecord> {
  return {
    ...group,
    senderKeyB64: await openField(group.senderKeyB64),
  };
}

async function openGroup(group: GroupRecord | undefined): Promise<GroupRecord | undefined> {
  if (!group) return undefined;
  return openGroupRow(group);
}

export async function getGroup(groupId: string): Promise<GroupRecord | undefined> {
  return openGroup(await db.groups.get(groupId));
}

export async function getGroupByConversation(conversationId: string): Promise<GroupRecord | undefined> {
  return openGroup(await db.groups.where("conversationId").equals(conversationId).first());
}

export async function listGroups(): Promise<GroupRecord[]> {
  const rows = await db.groups.toArray();
  return Promise.all(rows.map(openGroupRow));
}

export async function searchMessagesLocal(query: string, limit = 50): Promise<MessageEvent[]> {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  const all = await db.messages.toArray();
  // Caller must decrypt bodies before calling, or search ciphertext-safe fields only.
  return all
    .filter(
      (m) =>
        !m.deletedForMe &&
        !m.deletedForEveryone &&
        !m.body.startsWith("enc:v1:") &&
        m.body.toLowerCase().includes(q),
    )
    .sort((a, b) => b.createdAt - a.createdAt)
    .slice(0, limit);
}

/**
 * Secure local reset — deletes IndexedDB + clears in-memory at-rest cipher.
 * Does not touch remote relays. Caller SHOULD clear veto.* localStorage/sessionStorage
 * and reload into onboarding (SPEC §5.5 Data / secure reset).
 */
export async function wipeAllLocalData(): Promise<void> {
  setAtRestCipher(null);
  await db.delete();
}

/* ============================================================================
 * My stickers (FR-MSG sticker maker) — stickers the user made or saved.
 * Sealed at rest like drafts and sessions (SPEC §16): one `meta` row per
 * sticker, `sticker.mine.<id>` → `enc:v1:` AES-GCM(JSON {t, b, at}) under the
 * unlocked vault key. Same key layout as the native app's store. Writes fail
 * closed: without an active at-rest cipher nothing is persisted, so sticker
 * pixels never land in IndexedDB in the clear.
 * ========================================================================== */

export const MY_STICKERS_MAX = 16;
/** The maker exports 512×512; a photo PNG of that size fits comfortably. */
export const MY_STICKER_MAX_BYTES = 256 * 1024;
export const MY_STICKER_META_PREFIX = "sticker.mine.";

export type MyStickerMediaType = "image/png" | "image/webp" | "image/gif" | "image/jpeg";
const MY_STICKER_TYPES = new Set<string>(["image/png", "image/webp", "image/gif", "image/jpeg"]);
const MY_STICKER_ID_RE = /^[a-z0-9-]{6,40}$/;

export interface MySticker {
  id: string;
  mediaType: MyStickerMediaType;
  bytes: Uint8Array;
  /** Newest first in listings; editing a sticker moves it to the front. */
  updatedAt: number;
}

function myStickerBytesToB64(bytes: Uint8Array): string {
  let s = "";
  for (let i = 0; i < bytes.length; i += 0x8000) {
    s += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  }
  return btoa(s);
}

function myStickerB64ToBytes(b64: string): Uint8Array | null {
  try {
    const bin = atob(b64);
    const out = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
    return out;
  } catch {
    return null;
  }
}

function newMyStickerId(): string {
  const rnd = crypto.getRandomValues(new Uint8Array(4));
  return `${Date.now().toString(36)}-${[...rnd].map((b) => b.toString(16).padStart(2, "0")).join("")}`;
}

async function openMyStickerRow(row: MetaRecord): Promise<MySticker | null> {
  const id = row.key.slice(MY_STICKER_META_PREFIX.length);
  // Only sealed rows are trusted; a plaintext row is never rendered.
  if (!MY_STICKER_ID_RE.test(id) || typeof row.value !== "string" || !isSealedField(row.value)) {
    return null;
  }
  const opened = await openField(row.value);
  if (opened === row.value || opened.startsWith("[unable")) return null; // locked or foreign key
  try {
    const parsed = JSON.parse(opened) as { t?: unknown; b?: unknown; at?: unknown };
    if (typeof parsed.t !== "string" || !MY_STICKER_TYPES.has(parsed.t)) return null;
    if (typeof parsed.b !== "string") return null;
    const bytes = myStickerB64ToBytes(parsed.b);
    if (!bytes || bytes.length === 0 || bytes.length > MY_STICKER_MAX_BYTES) return null;
    return {
      id,
      mediaType: parsed.t as MyStickerMediaType,
      bytes,
      updatedAt: typeof parsed.at === "number" ? parsed.at : 0,
    };
  } catch {
    return null;
  }
}

function byNewest(a: MySticker, b: MySticker): number {
  return b.updatedAt - a.updatedAt || (a.id < b.id ? 1 : a.id > b.id ? -1 : 0);
}

async function openAllMyStickers(): Promise<MySticker[]> {
  const rows = await db.meta.where("key").startsWith(MY_STICKER_META_PREFIX).toArray();
  const opened = await Promise.all(rows.map(openMyStickerRow));
  return opened.filter((s): s is MySticker => s !== null).sort(byNewest);
}

/** Newest first, at most {@link MY_STICKERS_MAX}. Rows that cannot be opened are skipped. */
export async function listMyStickers(): Promise<MySticker[]> {
  return (await openAllMyStickers()).slice(0, MY_STICKERS_MAX);
}

export async function getMySticker(id: string): Promise<MySticker | undefined> {
  if (!MY_STICKER_ID_RE.test(id)) return undefined;
  const row = await db.meta.get(MY_STICKER_META_PREFIX + id);
  return (row && (await openMyStickerRow(row))) || undefined;
}

/**
 * Save a sticker (or replace `replaceId` after editing it). Keeps the newest
 * {@link MY_STICKERS_MAX}; the oldest one is dropped when the set is full,
 * like the phone. Throws when the vault is locked, the type is not a raster
 * image, or the bytes exceed {@link MY_STICKER_MAX_BYTES}.
 */
export async function saveMySticker(input: {
  bytes: Uint8Array;
  mediaType: string;
  replaceId?: string;
  updatedAt?: number;
}): Promise<MySticker> {
  if (!getAtRestCipher()) throw new Error("Unlock Veto to save stickers.");
  if (!MY_STICKER_TYPES.has(input.mediaType)) throw new Error("Stickers must be PNG, WebP, GIF or JPEG.");
  if (input.bytes.length === 0) throw new Error("Empty sticker.");
  if (input.bytes.length > MY_STICKER_MAX_BYTES) {
    throw new Error(`Sticker too large (max ${Math.round(MY_STICKER_MAX_BYTES / 1024)} KiB).`);
  }
  const id = input.replaceId && MY_STICKER_ID_RE.test(input.replaceId) ? input.replaceId : newMyStickerId();
  const updatedAt = input.updatedAt ?? Date.now();
  const sealed = await sealField(
    JSON.stringify({ t: input.mediaType, b: myStickerBytesToB64(input.bytes), at: updatedAt }),
  );
  // The cipher can disappear mid-call (auto-lock): never write the clear JSON.
  if (!isSealedField(sealed)) throw new Error("Unlock Veto to save stickers.");
  await db.meta.put({ key: MY_STICKER_META_PREFIX + id, value: sealed });
  const overflow = (await openAllMyStickers()).slice(MY_STICKERS_MAX);
  if (overflow.length) await db.meta.bulkDelete(overflow.map((s) => MY_STICKER_META_PREFIX + s.id));
  return { id, mediaType: input.mediaType as MyStickerMediaType, bytes: input.bytes, updatedAt };
}

export async function deleteMySticker(id: string): Promise<void> {
  if (!MY_STICKER_ID_RE.test(id)) return;
  await db.meta.delete(MY_STICKER_META_PREFIX + id);
}
